import { Typography, Box } from '@mui/material';
import { Card } from '@mui/material';
import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import CustomCard from './CustomCard';
import { LoginForm } from '../../components/authentication/login';
import { MHidden } from '../../components/@material-extend';
import HelpDialog from './HelpDialog';

const MainContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minHeight: '100vh',
  padding: '120px 20px 60px',
  maxWidth: '1200px',
  margin: '0 auto',
  width: '100%',
  
  [theme.breakpoints.down('md')]: {
    padding: '100px 16px 40px',
    maxWidth: '600px'
  }
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: '32px',
  
  [theme.breakpoints.up('lg')]: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: '40px'
  }
}));

const LoginSection = styled(Box)(({ theme }) => ({
  flex: '1',
  minWidth: 0,
  
  [theme.breakpoints.up('lg')]: {
    flex: '2'
  }
}));

const WelcomeSection = styled(Box)(({ theme }) => ({
  display: 'none',
  
  [theme.breakpoints.up('lg')]: {
    display: 'flex',
    flex: '1',
    minWidth: 0
  }
}));

const LoginCard = styled(Card)(({ theme }) => ({
  padding: '20px',
  height: '100%',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  
  [theme.breakpoints.down('sm')]: {
    padding: '16px'
  }
}));

const WelcomeCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: '400px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '40px 20px',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  backgroundColor: 'white',
  color: 'inherit',
  
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    marginBottom: '20px'
  }
}));

const CardsSection = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '24px',
  width: '100%',
  
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '32px'
  },
  
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '40px'
  }
}));

const HelpButton = styled(Typography)(({ theme }) => ({
  cursor: 'pointer',
  color: '#00AB55',
  fontWeight: 'bold',
  fontSize: '1.1rem',
  textAlign: 'center',
  marginTop: '20px',
  padding: '8px 16px',
  borderRadius: '8px',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    backgroundColor: 'rgba(0, 171, 85, 0.1)',
    transform: 'translateY(-2px)'
  }
}));

function MainCards(props) {
  const { items } = props;
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  return (
    <>
      <MainContainer>
        {/* Mobile View */}
        <MHidden width="mdUp">
          <LoginSection>
            <LoginCard>
              <Typography
                variant="subtitle2"
                sx={{
                  textAlign: 'center',
                  mb: 2,
                  color: 'text.secondary'
                }}
              >
                If you don't have an account, please register with one of the options below.
              </Typography>
              <LoginForm />
            </LoginCard>
          </LoginSection>
          
          <CardsSection>
            {items.map((product) => (
              <CustomCard key={product.id} product={product} />
            ))}
          </CardsSection>
        </MHidden>

        {/* Desktop View */}
        <MHidden width="mdDown">
          <ContentWrapper>
            <LoginSection>
              <LoginCard>
                <Typography
                  variant="h6"
                  sx={{
                    textAlign: 'center',
                    mb: 3,
                    color: 'text.primary',
                    fontWeight: 600
                  }}
                >
                  If you don't have an account, please register with one of the options below.
                </Typography>
                <LoginForm />
              </LoginCard>
            </LoginSection>
            
            <WelcomeSection>
              <WelcomeCard>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    mb: 3, 
                    textAlign: 'center',
                    fontWeight: 700
                  }}
                >
                  Hi, Welcome Back
                </Typography>
                <img 
                  src="/static/illustrations/illustration_login.png" 
                  alt="login" 
                  style={{ maxWidth: '200px' }}
                />
                <HelpButton
                  onClick={() => setHelpDialogOpen(true)}
                >
                  Need Help?
                </HelpButton>
              </WelcomeCard>
            </WelcomeSection>
          </ContentWrapper>
          
          <CardsSection sx={{ mt: 4 }}>
            {items.map((product) => (
              <CustomCard key={product.id} product={product} />
            ))}
          </CardsSection>
        </MHidden>
      </MainContainer>
      
      {helpDialogOpen && <HelpDialog closeDialog={() => setHelpDialogOpen(false)} />}
    </>
  );
}

MainCards.propTypes = {
  items: PropTypes.array
};

export default MainCards;
